/**
 * What a master will be called, worked out while the form is being filled.
 *
 * The server composes the name that counts, from the same parts: the type
 * a person types, the source they pick, and whether it is generic. This
 * mirrors that so the form can show the result before anything is
 * created, and the modal's second step puts it in front of them.
 *
 * It deliberately does not clean up more than whitespace and case. A
 * person who types "product master kroger" with Kroger selected gets
 * `product_master_kroger_master_kroger`, and sees exactly that before
 * confirming — guessing at what they meant would quietly rename a master
 * whose type legitimately ends in its source's name.
 *
 * The same file exists in the admin. Two apps with no shared package, so
 * it is copied rather than imported; if they ever disagree, the
 * confirmation step is where it shows.
 */

/** What a master carries when it is tied to no single source. */
export const GENERIC_TAG = 'generic';

export type MasterName = {
  /** What the pipeline addresses it by, and the S3 prefix it lands in. */
  slug: string;
  /** What a person reads, here and in the admin's table. */
  displayName: string;
  /** What the file is called in the library. */
  fileName: string;
  /** Who it is for, in the vocabulary access is granted in. */
  tags: string[];
};

/**
 * Whitespace and punctuation to single underscores, lowercased.
 *
 * This much is safe to do silently: it removes no meaning, and the
 * server refuses anything that is not already in this shape.
 */
export function normaliseType(masterType: string): string {
  return masterType
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Whether the server will accept this as the typed part of a name. */
export function isValidType(normalised: string): boolean {
  return /^[a-z][a-z0-9_]{0,40}$/.test(normalised);
}

/**
 * Every name the choice derives into.
 *
 * `sourceName` is the source's short name — `walmart`, not `Walmart` —
 * because it goes into the slug as it is. Null means generic.
 */
export function masterNameFor(masterType: string, sourceName: string | null): MasterName {
  const type = normaliseType(masterType);
  const slug = sourceName === null ? `${type}_master` : `${type}_master_${sourceName}`;

  const words = slug.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1));

  return {
    slug,
    displayName: words.join(' '),
    fileName: `${words.join('')}.xlsx`,
    tags: sourceName === null ? [GENERIC_TAG] : [sourceName],
  };
}
